import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lock, CheckCircle, Zap, TrendingUp, Users, Globe, 
  Smartphone, QrCode, Wifi, BarChart3, Shield, DollarSign,
  ArrowRight, Star, Target, Layers, Database, Mail,
  ChevronDown, ChevronUp, Play, Award, Clock
} from 'lucide-react';

const InvestorDeck = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);

  const CORRECT_PASSWORD = process.env.REACT_APP_INVESTOR_DECK_PASSWORD || 'scanit2go123';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      setAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password');
    }
  };

  // Password Gate
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full border border-white/20"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Investor Materials</h1>
            <p className="text-white/60">Enter password to access the deck</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500"
              autoFocus
            />
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            <button
              type="submit"
              className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all"
            >
              Access Deck
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Deck Content
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold">ScanIt2Go</span>
          </div>
          <div className="text-sm text-white/60">Investor Deck 2025</div>
        </div>
      </nav>

      <div className="pt-20 pb-12 px-6">
        <div className="max-w-6xl mx-auto space-y-24">

          {/* Hero Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm mb-6">
              <Star className="w-4 h-4 text-yellow-400" />
              <span>Series A Ready</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-purple-200 to-indigo-200 bg-clip-text text-transparent">
              The Future of<br />Physical-Digital Connection
            </h1>
            <p className="text-xl text-white/70 max-w-2xl mx-auto mb-8">
              NFC + QR platform turning physical products into engaging digital experiences. 
              Capturing first-party data where competitors can't.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="px-6 py-3 bg-white/10 rounded-xl">
                <div className="text-2xl font-bold">$5.2B</div>
                <div className="text-sm text-white/60">Market by 2028</div>
              </div>
              <div className="px-6 py-3 bg-white/10 rounded-xl">
                <div className="text-2xl font-bold">47%</div>
                <div className="text-sm text-white/60">CAGR Growth</div>
              </div>
              <div className="px-6 py-3 bg-white/10 rounded-xl">
                <div className="text-2xl font-bold">2 Markets</div>
                <div className="text-sm text-white/60">B2B + D2C</div>
              </div>
            </div>
          </motion.section>

          {/* Problem Section */}
          <section className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">The Problem</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-red-400">✕</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Broken Attribution</h3>
                    <p className="text-white/60 text-sm">Brands can't track offline-to-online conversions</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-red-400">✕</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Cookie Apocalypse</h3>
                    <p className="text-white/60 text-sm">Third-party tracking is dying, brands need first-party data</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-red-400">✕</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Static Products</h3>
                    <p className="text-white/60 text-sm">Physical products can't update after they ship</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-3xl p-8 border border-white/10">
              <h3 className="text-xl font-semibold mb-4 text-center">Market Pain Points</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Brands losing visibility</span>
                  <span className="font-bold">73%</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full" style={{width: '73%'}}></div>
                </div>
                <div className="flex justify-between items-center mt-4">
                  <span className="text-white/70">Need first-party data</span>
                  <span className="font-bold">89%</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full" style={{width: '89%'}}></div>
                </div>
              </div>
            </div>
          </section>

          {/* Solution Section */}
          <section>
            <h2 className="text-3xl font-bold mb-8 text-center">Our Solution</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                  <Wifi className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-2">NFC + QR Hybrid</h3>
                <p className="text-white/60">Only platform combining NFC tags with QR codes. Premium tap experience + fallback scanning.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                  <Play className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Rich Media Content</h3>
                <p className="text-white/60">Videos, audio, photos - not just links. Create emotional experiences that competitors can't match.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                  <Database className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-2">First-Party Data</h3>
                <p className="text-white/60">Lead capture at the point of physical interaction. CRM webhooks to HubSpot, Salesforce, Zapier.</p>
              </div>
            </div>
          </section>

          {/* Features Grid */}
          <section>
            <h2 className="text-3xl font-bold mb-8 text-center">Platform Capabilities</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: QrCode, title: "Dynamic QR Codes", desc: "Update content anytime" },
                { icon: Wifi, title: "NFC Tags", desc: "Premium tap experience" },
                { icon: BarChart3, title: "Real-time Analytics", desc: "Live scan counter & alerts" },
                { icon: Globe, title: "Geographic Heat Maps", desc: "Visual world map of scans" },
                { icon: Mail, title: "Lead Capture", desc: "Email collection on view" },
                { icon: Zap, title: "CRM Webhooks", desc: "HubSpot, Salesforce, Zapier" },
                { icon: Shield, title: "Enterprise Security", desc: "Multi-tenant, encrypted" },
                { icon: Layers, title: "White-Label", desc: "Resell to your clients" },
                { icon: Database, title: "Bulk API", desc: "Create 1000s of tags" },
                { icon: Target, title: "UTM Tracking", desc: "Campaign attribution" },
                { icon: Users, title: "CSV Exports", desc: "Download leads & analytics" },
                { icon: Smartphone, title: "Video Transcoding", desc: "Works on all devices" },
                { icon: Award, title: "AI Chatbots", desc: "Marketing + Admin assistants" },
                { icon: DollarSign, title: "PDF Reports", desc: "Branded analytics reports" },
                { icon: Clock, title: "Custom Date Range", desc: "Any time period analysis" },
                { icon: TrendingUp, title: "Time Heatmaps", desc: "Best day/hour insights" },
              ].map((feature, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500/30 to-purple-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">{feature.title}</h4>
                    <p className="text-white/50 text-xs">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Competitive Advantage */}
          <section>
            <h2 className="text-3xl font-bold mb-8 text-center">vs. Flowcode & Competitors</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4">Feature</th>
                    <th className="text-center py-4 px-4 bg-gradient-to-r from-indigo-500/20 to-purple-500/20">ScanIt2Go</th>
                    <th className="text-center py-4 px-4">Flowcode</th>
                    <th className="text-center py-4 px-4">Beaconstac</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    ["NFC Support", true, false, true],
                    ["Rich Media (Video/Audio)", true, false, false],
                    ["Geographic Heat Maps", true, true, false],
                    ["Real-time Live Counter", true, false, false],
                    ["Lead Capture", true, true, true],
                    ["CRM Webhooks", true, true, true],
                    ["AI Chatbots", true, false, false],
                    ["PDF Reports", true, true, false],
                    ["Custom Date Range", true, true, true],
                    ["Bulk API (1000+ tags)", true, "Enterprise", "Enterprise"],
                    ["CSV Exports", true, true, true],
                    ["White-Label Platform", true, "Enterprise", "Enterprise"],
                    ["Video Transcoding", true, false, false],
                    ["Starting Price", "$39/mo", "$100+/mo", "$60/mo"],
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-3 px-4 text-white/70">{row[0]}</td>
                      <td className="py-3 px-4 text-center bg-gradient-to-r from-indigo-500/10 to-purple-500/10">
                        {row[1] === true ? <CheckCircle className="w-5 h-5 text-green-400 mx-auto" /> : 
                         row[1] === false ? <span className="text-red-400">✕</span> : 
                         <span className="text-white/70">{row[1]}</span>}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {row[2] === true ? <CheckCircle className="w-5 h-5 text-green-400 mx-auto" /> : 
                         row[2] === false ? <span className="text-red-400">✕</span> : 
                         <span className="text-white/70">{row[2]}</span>}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {row[3] === true ? <CheckCircle className="w-5 h-5 text-green-400 mx-auto" /> : 
                         row[3] === false ? <span className="text-red-400">✕</span> : 
                         <span className="text-white/70">{row[3]}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Business Model */}
          <section className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6">Business Model</h2>
              <div className="space-y-4">
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    B2B SaaS (Primary)
                  </h3>
                  <p className="text-white/60 text-sm mb-3">Marketing agencies, retail brands, event companies</p>
                  <div className="flex gap-4 text-sm">
                    <div className="px-3 py-1 bg-white/10 rounded-lg">Starter: $39/mo</div>
                    <div className="px-3 py-1 bg-white/10 rounded-lg">Pro: $99/mo</div>
                    <div className="px-3 py-1 bg-white/10 rounded-lg">Enterprise: Custom</div>
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    D2C / Consumer
                  </h3>
                  <p className="text-white/60 text-sm mb-3">Gifts, awards, weddings, personal memories</p>
                  <div className="flex gap-4 text-sm">
                    <div className="px-3 py-1 bg-white/10 rounded-lg">Per-tag pricing</div>
                    <div className="px-3 py-1 bg-white/10 rounded-lg">Marketplace potential</div>
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-purple-400" />
                    White-Label Revenue
                  </h3>
                  <p className="text-white/60 text-sm">Agencies resell to their clients. Revenue share model.</p>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6">Target Markets</h2>
              <div className="space-y-3">
                {[
                  { market: "Marketing Agencies", tam: "$2.1B", growth: "32%" },
                  { market: "Retail & CPG", tam: "$1.8B", growth: "28%" },
                  { market: "Events & Hospitality", tam: "$890M", growth: "41%" },
                  { market: "Gifts & Personalization", tam: "$420M", growth: "23%" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-xl">
                    <span className="font-medium">{item.market}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-white/60">{item.tam}</span>
                      <span className="text-green-400 text-sm">+{item.growth}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Roadmap */}
          <section>
            <h2 className="text-3xl font-bold mb-8 text-center">Product Roadmap</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6">
                <div className="text-green-400 text-sm font-semibold mb-3">✓ COMPLETED</div>
                <h3 className="text-xl font-semibold mb-4">Core Platform</h3>
                <ul className="space-y-2 text-sm text-white/70">
                  <li>• NFC + QR tag management</li>
                  <li>• Rich media upload & playback</li>
                  <li>• Geographic heat maps</li>
                  <li>• Real-time live analytics</li>
                  <li>• Custom date range reports</li>
                  <li>• PDF report generation</li>
                  <li>• Lead capture forms</li>
                  <li>• CRM webhooks</li>
                  <li>• AI chatbot assistants</li>
                  <li>• White-label infrastructure</li>
                  <li>• Bulk API (1000+ tags)</li>
                  <li>• CSV exports</li>
                  <li>• UTM tracking</li>
                </ul>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6">
                <div className="text-yellow-400 text-sm font-semibold mb-3">→ IN PROGRESS</div>
                <h3 className="text-xl font-semibold mb-4">Growth Features</h3>
                <ul className="space-y-2 text-sm text-white/70">
                  <li>• Conditional forms & routing</li>
                  <li>• A/B testing for content</li>
                  <li>• AI-generated QR designs</li>
                  <li>• Landing page templates</li>
                  <li>• Native Zapier integration</li>
                  <li>• Email scan notifications</li>
                  <li>• Conversion tracking</li>
                </ul>
              </div>
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-2xl p-6">
                <div className="text-purple-400 text-sm font-semibold mb-3">◇ PLANNED</div>
                <h3 className="text-xl font-semibold mb-4">Scale & Enterprise</h3>
                <ul className="space-y-2 text-sm text-white/70">
                  <li>• Full landing page builder</li>
                  <li>• Advanced segmentation</li>
                  <li>• SSO / SAML</li>
                  <li>• Mobile SDK</li>
                  <li>• Partner marketplace</li>
                  <li>• Multi-language support</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Ask Section */}
          <section className="text-center py-12">
            <h2 className="text-3xl font-bold mb-6">The Ask</h2>
            <div className="inline-block bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-white/20 rounded-2xl p-8 max-w-2xl">
              <div className="text-5xl font-bold mb-4 bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                $1.5M Seed
              </div>
              <p className="text-white/70 mb-6">18-month runway to achieve product-market fit and initial scale</p>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="p-3 bg-white/5 rounded-xl">
                  <div className="font-bold text-xl">40%</div>
                  <div className="text-white/50">Engineering</div>
                </div>
                <div className="p-3 bg-white/5 rounded-xl">
                  <div className="font-bold text-xl">35%</div>
                  <div className="text-white/50">Sales & Marketing</div>
                </div>
                <div className="p-3 bg-white/5 rounded-xl">
                  <div className="font-bold text-xl">25%</div>
                  <div className="text-white/50">Operations</div>
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="text-center pb-12">
            <div className="inline-flex flex-col items-center gap-4">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
                <Zap className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold">Let's Connect</h2>
              <a 
                href="mailto:andy@scanit2go.com" 
                className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all flex items-center gap-2"
              >
                andy@scanit2go.com
                <ArrowRight className="w-5 h-5" />
              </a>
              <p className="text-white/50 text-sm">ScanIt2Go © 2025</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default InvestorDeck;
